import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { normalizeUpc } from 'fym-shared/src/music24/catalogAddSong';
import { TypedMemo } from '../../../../common/types';
import { usePatchCatalogSongMutation } from '../../../../../redux/api/fymAPI';
import { CatalogItem, CatalogSong } from '../../../../../redux/api/fymAPI.types';
import { isIsrcAcceptable } from '../../song/IsrcField';
import { normalizeIsrc } from '../../song/normalizeIsrc';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { TrackSave } from '../useSavingCount';
import { StepHeader } from '../StepHeader';
import { StepActions } from '../StepActions';
import { WizardIsrcField } from './WizardIsrcField';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The longest UPC (EAN-14): the field takes no more (QA brk-19). */
const UPC_MAX_DIGITS = 14;

interface CodesStepProps {
  accountId: number;
  item: CatalogItem;
  song: CatalogSong | undefined;
  eyebrow: string;
  track: TrackSave;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  /** "Already out? Link it instead" (design b2): the link question of step 3. */
  onLinkInstead: () => void;
}

/** True when the text is empty (no UPC) or a good UPC. */
export function isUpcAcceptable(value: string): boolean {
  return value.trim() === '' || normalizeUpc(value) !== null;
}

/** The stored value of a code field: null for an empty field. */
function codeOf(value: string, normalize: (text: string) => string | null): string | null {
  return value.trim() === '' ? null : normalize(value);
}

/**
 * Step 3 of an upcoming song (design catalog-add-song b3, b4): the ISRC and the UPC, both optional.
 * `PATCH .../catalog/:uuid/song { isrc, upc }` with the changed fields only.
 */
const CodesStepComponent: React.FC<CodesStepProps> = ({
  accountId,
  item,
  song,
  eyebrow,
  track,
  onBack,
  onNext,
  onSkip,
  onLinkInstead,
}) => {
  const intl = useIntl();
  const [isrc, setIsrc] = useState(song?.isrc ?? '');
  const [upc, setUpc] = useState(song?.upc ?? '');
  const [isForced, setForced] = useState(false);
  const [isUpcTouched, setUpcTouched] = useState(false);
  const [hasFailed, setFailed] = useState(false);
  const [patchSong, patching] = usePatchCatalogSongMutation();
  const showUpcError = (isUpcTouched || isForced) && !isUpcAcceptable(upc);

  const save = useCallback(() => {
    if (!isIsrcAcceptable(isrc) || !isUpcAcceptable(upc)) {
      setForced(true);
      return;
    }
    const isrcValue = codeOf(isrc, normalizeIsrc);
    const upcValue = codeOf(upc, normalizeUpc);
    const fields: { isrc?: string | null; upc?: string | null } = {};
    if (isrcValue !== (song?.isrc ?? null)) {
      fields.isrc = isrcValue;
    }
    if (upcValue !== (song?.upc ?? null)) {
      fields.upc = upcValue;
    }
    if (Object.keys(fields).length === 0) {
      onNext();
      return;
    }
    setFailed(false);
    track(patchSong({ accountId, itemUuid: item.uuid, ...fields }).unwrap())
      .then(() => onNext())
      .catch(() => setFailed(true));
  }, [accountId, isrc, item.uuid, onNext, patchSong, song, track, upc]);

  return (
    <>
      <StepHeader
        eyebrow={eyebrow}
        question={intl.formatMessage(addSongFlowMessages.codesQuestion)}
        sub={intl.formatMessage(addSongFlowMessages.codesSub, { title: item.title })}
      />
      <div className={styles.two}>
        <WizardIsrcField value={isrc} onChange={setIsrc} disabled={patching.isLoading} forceError={isForced} />
        <div className={styles.field}>
          <label className={styles.label} htmlFor="add-song-upc">
            {intl.formatMessage(addSongFlowMessages.upc)}{' '}
            <span className={styles.optional}>· {intl.formatMessage(addSongFlowMessages.optional)}</span>
          </label>
          <input
            id="add-song-upc"
            className={clsx(styles.input, styles.inputCode, showUpcError && styles.inputBad)}
            value={upc}
            inputMode="numeric"
            autoComplete="off"
            placeholder="012345678905"
            disabled={patching.isLoading}
            aria-invalid={showUpcError}
            // Numbers only, at most 14: a space or a dash of a pasted code goes (`normalizeUpc` ignores
            // them too). No `maxLength`: it would cut a pasted "0 12345 67890 5" before the clean-up.
            onChange={(event) => setUpc(event.target.value.replace(/\D/g, '').slice(0, UPC_MAX_DIGITS))}
            onBlur={() => setUpcTouched(true)}
          />
          {showUpcError ? (
            <span className={styles.error} role="alert">
              {intl.formatMessage(addSongFlowMessages.upcError)}
            </span>
          ) : (
            <span className={styles.hint}>{intl.formatMessage(addSongFlowMessages.upcHint)}</span>
          )}
        </div>
      </div>
      <p className={styles.hint}>{intl.formatMessage(addSongFlowMessages.codesLater)}</p>
      <div>
        <button type="button" className={styles.linkButton} onClick={onLinkInstead} data-testid="add-song-link-instead">
          {intl.formatMessage(addSongFlowMessages.codesAlreadyOut)}
        </button>
      </div>
      {hasFailed && (
        <div className={styles.alertError} role="alert">
          {intl.formatMessage(addSongFlowMessages.saveFailed)}
        </div>
      )}
      <StepActions
        onBack={onBack}
        onSkip={onSkip}
        main={{
          label: intl.formatMessage(hasFailed ? addSongFlowMessages.tryAgain : addSongFlowMessages.next),
          onClick: save,
          disabled: isForced && (!isIsrcAcceptable(isrc) || !isUpcAcceptable(upc)),
          isLoading: patching.isLoading,
        }}
      />
    </>
  );
};

export const CodesStep = TypedMemo(CodesStepComponent);
